import styled from '@emotion/styled';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import React from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { PROJECT_SETTINGS_TAB_LABELS } from '../admin/tab-constants';
import UserNavigationMenu from '../settings/user-navigation-menu';

import { DesignToken } from '~/client/components/base/core';
import { MedicalHeartrateReport } from '~c/components/base/icon';
import { LayoutDashboard } from '~c/components/base/icon/layout-dashboard-1';
import {
  LeftNavFlyoutMenuItem,
  LeftNavMenu,
  LeftNavMenuGroup,
  LeftNavMenuItem,
  useLeftNavContext,
} from '~c/components/base/left-nav';
import {
  aiDashboardUtils,
  dashboardRouteUtil,
  projectMembersRouteUtil,
  projectRouteUtil,
  settingsRoutesUtil,
} from '~c/routes';

interface Props {
  className?: string;
}

const ProjectImpl: React.FC<Props> = ({ className }) => {
  const { matchRoute: DashboardMatchRoute, navigateTo: navigateToDashboard } =
    projectRouteUtil.useHelper();
  projectMembersRouteUtil.useHelper();
  const dashboard = dashboardRouteUtil.useHelper();
  const { navigateTo: navigateToAiSystem } = aiDashboardUtils.useHelper();
  const { navigateTo: navigateToSettings } = settingsRoutesUtil.useHelper();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { state } = useLeftNavContext();
  const { currentRoute } = state;
  const { projectId } = useParams<{ projectId: string }>();

  const isOnSettings = location.pathname.endsWith('/settings');
  const activeTabParam = searchParams.get('tab');

  const routePrefix = {
    [projectMembersRouteUtil.routePrefix]: projectMembersRouteUtil.routePrefix,
    [projectRouteUtil.routePrefix]: projectRouteUtil.routePrefix,
  };

  return (
    <LeftNavMenu
      className={className}
      name={routePrefix[currentRoute ?? '']}
      header={{
        showBackButton: true,
        onClick: () => dashboard.navigateTo('COMPLIANCE'),
      }}
    >
      <LeftNavMenuItem
        selected={DashboardMatchRoute('DASHBOARD')}
        icon={LayoutDashboard}
        onClick={() => navigateToDashboard('DASHBOARD')}
      >
        Dashboard
      </LeftNavMenuItem>
      <LeftNavMenuItem
        selected={DashboardMatchRoute('REPORT')}
        icon={MedicalHeartrateReport}
        onClick={() => navigateToDashboard('REPORT')}
      >
        Reports
      </LeftNavMenuItem>

      <LeftNavMenuGroup
        icon={SettingsOutlinedIcon}
        title="Settings"
        expandedIcon={ExpandLessIcon}
        collapsedIcon={ExpandMoreIcon}
        expanded={isOnSettings}
        onToggle={() => {
          if (!isOnSettings) {
            if (projectId) {
              navigateToAiSystem('SETTINGS', { params: { projectId } });
            } else {
              navigateToSettings('PERSONAL');
            }
          }
        }}
      >
        {Object.entries(PROJECT_SETTINGS_TAB_LABELS).map(
          ([indexStr, label]) => {
            const tabIndex = parseInt(indexStr, 10);
            const isActive =
              isOnSettings &&
              (activeTabParam
                ? activeTabParam === String(tabIndex)
                : tabIndex === 0);
            return (
              <LeftNavFlyoutMenuItem
                key={tabIndex}
                selected={isActive}
                onClick={() => {
                  if (projectId) {
                    navigate(
                      `/ai-system/${projectId}/settings?tab=${tabIndex}`
                    );
                  }
                }}
              >
                {label}
              </LeftNavFlyoutMenuItem>
            );
          }
        )}
      </LeftNavMenuGroup>

      <UserNavigationMenu />
    </LeftNavMenu>
  );
};

const Project = styled(ProjectImpl)`
  .grow {
    flex-grow: 1;
  }
  .userinfo {
    padding: 16px;
    text-transform: capitalize;
  }
  .task-menu-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
  .task-badge {
    background-color: ${DesignToken.status.danger};
    color: ${DesignToken.brand.primaryContrast};
    border-radius: 10px;
    padding: 2px 8px;
    font-size: 12px;
    margin-left: 8px;
    font-weight: 500;
  }

  .v2-header {
    margin-left: -10px;
  }
`;

export default Project;
